import { useRef } from 'react';
import ReactDatetime from 'react-datetime';
import moment from 'moment';
import 'react-datetime/css/react-datetime.css';
import { colors, layout } from '../styles';

interface DateInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

const toMoment = (value: string): moment.Moment | string => {
  if (!value) return '';
  if (ISO_DATE_RE.test(value)) {
    const parsed = moment(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : value;
  }
  const parsed = moment(value, 'MM/DD/YYYY', true);
  return parsed.isValid() ? parsed : value;
};

const DateInput = ({ value, onChange, placeholder = 'mm/dd/yyyy', disabled }: DateInputProps) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleChange = (next: any) => {
    if (!next) {
      onChange('');
      return;
    }
    if (typeof next === 'string') {
      onChange(next);
      return;
    }
    if (moment.isMoment(next) && next.isValid()) {
      onChange(next.format('YYYY-MM-DD'));
      return;
    }
    onChange(String(next));
  };

  const focusInputFromAnywhere = () => {
    if (disabled) return;
    const input = wrapperRef.current?.querySelector('input');
    if (input) {
      input.focus();
      input.click();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="v2-date-input"
      onClick={focusInputFromAnywhere}
      style={{ position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <ReactDatetime
        value={toMoment(value) as any}
        onChange={handleChange}
        dateFormat="MM/DD/YYYY"
        timeFormat={false}
        closeOnSelect
        closeOnClickOutside
        inputProps={{
          placeholder,
          disabled,
          readOnly: true,
          style: {
            ...layout.input,
            ...(disabled ? { background: '#E5E7EB', cursor: 'not-allowed' } : { cursor: 'pointer' }),
            paddingRight: 36,
          },
        }}
      />
      <i
        className="fas fa-calendar-alt"
        style={{
          position: 'absolute',
          right: 12,
          top: '50%',
          transform: 'translateY(-50%)',
          color: colors.muted,
          pointerEvents: 'none',
          fontSize: 14,
        }}
      />
    </div>
  );
};

export default DateInput;
